import { MEDIA_LIBRARY_ASSETS } from './CMSParams';

const VIDEO_MIME_PREFIX = 'video/';
const VIDEO_URL_EXTENSIONS_REGEX = /\.(mp4|mov|m4v|webm|m3u8)$/i;
const MEDIA_LIBRARY_ASSETS_STORAGE_KEY = 'studio.mediaLibraryAssets';

const normalizeAssetType = ({ assetType, mimeType, url }) => {
  if (assetType === 'video' || assetType === 'image') {
    return assetType;
  }

  if (
    typeof mimeType === 'string' &&
    mimeType.toLowerCase().startsWith(VIDEO_MIME_PREFIX)
  ) {
    return 'video';
  }

  if (typeof url === 'string' && VIDEO_URL_EXTENSIONS_REGEX.test(url)) {
    return 'video';
  }

  return 'image';
};

const parseAssetNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const normalizeMediaLibraryAssets = (assets) => {
  if (!Array.isArray(assets)) {
    return [];
  }

  return assets
    .filter((asset) => asset && typeof asset.url === 'string' && asset.url)
    .map((asset) => ({
      id: typeof asset.id === 'string' ? asset.id : '',
      title: typeof asset.title === 'string' ? asset.title : '',
      url: asset.url,
      playcardUrl:
        typeof asset.playcardUrl === 'string' && asset.playcardUrl
          ? asset.playcardUrl
          : asset.url,
      mimeType: typeof asset.mimeType === 'string' ? asset.mimeType : '',
      width: parseAssetNumber(asset.width),
      height: parseAssetNumber(asset.height),
      assetType: normalizeAssetType(asset),
    }));
};

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const safeDecodeURIComponent = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const readPersistedMediaLibraryAssets = () => {
  try {
    const persistedAssets = window.sessionStorage?.getItem(
      MEDIA_LIBRARY_ASSETS_STORAGE_KEY
    );

    if (!persistedAssets) {
      return [];
    }

    const parsedAssets = safeJsonParse(persistedAssets);
    const normalizedAssets = normalizeMediaLibraryAssets(parsedAssets);
    return normalizedAssets;
  } catch {
    return [];
  }
};

const persistMediaLibraryAssets = (assets) => {
  try {
    window.sessionStorage?.setItem(
      MEDIA_LIBRARY_ASSETS_STORAGE_KEY,
      JSON.stringify(assets)
    );
  } catch {
    return;
  }
};

export const clearPersistedMediaLibraryAssets = () => {
  try {
    window.sessionStorage?.removeItem(MEDIA_LIBRARY_ASSETS_STORAGE_KEY);
  } catch {
    return;
  }
};

export const parseMediaLibraryAssetsFromHash = (hash) => {
  const hashValue = hash || window.location.hash;

  if (!hashValue) {
    return readPersistedMediaLibraryAssets();
  }

  const hashParams = new URLSearchParams(
    hashValue.startsWith('#') ? hashValue.slice(1) : hashValue
  );
  const rawAssets = hashParams.get(MEDIA_LIBRARY_ASSETS);

  if (!rawAssets) {
    return readPersistedMediaLibraryAssets();
  }

  const decodedAssets =
    safeJsonParse(rawAssets) || safeJsonParse(safeDecodeURIComponent(rawAssets));

  const normalizedAssets = normalizeMediaLibraryAssets(decodedAssets);

  if (normalizedAssets.length) {
    persistMediaLibraryAssets(normalizedAssets);
    return normalizedAssets;
  }

  return readPersistedMediaLibraryAssets();
};

export const removeMediaLibraryAssetsFromHash = (hash) => {
  const hashValue = hash || window.location.hash;

  if (!hashValue) {
    return;
  }

  const hashParams = new URLSearchParams(
    hashValue.startsWith('#') ? hashValue.slice(1) : hashValue
  );

  if (!hashParams.has(MEDIA_LIBRARY_ASSETS)) {
    return;
  }

  hashParams.delete(MEDIA_LIBRARY_ASSETS);

  const nextHash = hashParams.toString();
  const nextUrl = `${window.location.pathname}${window.location.search}${
    nextHash ? `#${nextHash}` : ''
  }`;

  window.history.replaceState(null, null, nextUrl);
};
